import normalizeText from "utils/normalizeText";

/**
 * Fallback locator for findSequentialNodes.
 *
 * findSequentialNodes only matches when the quoted text starts AND ends exactly
 * on a text-node boundary (it compares whole nodes). A PDF text layer splits by
 * rendered line, so a quoted sentence usually begins part-way through a span and
 * ends part-way through another — nothing matches and nothing is highlighted.
 *
 * This locator works at character level instead: it normalises the page one
 * character at a time (same rules as normalizeText), keeping a map back to the
 * text node and offset each surviving character came from, then finds the target
 * as a plain substring. Returns null when the text is not on the page at all.
 */
export const locateTextRange = (searchText, rootNode) => {
  const target = normalizeText(searchText || "");

  if (!target || !rootNode) {
    return null;
  }

  const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT);
  let normalized = "";
  // One entry per character of `normalized`, holding where it came from.
  const origins = [];

  while (walker.nextNode()) {
    const node = walker.currentNode;
    const raw = node.textContent || "";

    for (let index = 0; index < raw.length; index += 1) {
      // Per character, so an expanding lower-case mapping still yields one
      // origin per normalised character rather than shifting every index after it.
      const kept = normalizeText(raw[index]);

      for (const character of kept) {
        normalized += character;
        origins.push({ node, offset: index });
      }
    }
  }

  const start = normalized.indexOf(target);

  if (start === -1) {
    return null;
  }

  const first = origins[start];
  const last = origins[start + target.length - 1];

  return {
    startNode: first.node,
    startOffset: first.offset,
    endNode: last.node,
    // The range is exclusive of endOffset, and `last` points at the character
    // itself, so the caller needs the offset just past it.
    endOffset: last.offset + 1,
  };
};

const textNodesBetween = (rootNode, startNode, endNode) => {
  const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT);
  const nodes = [];
  let started = false;

  while (walker.nextNode()) {
    const node = walker.currentNode;

    if (node === startNode) {
      started = true;
    }
    if (started) {
      nodes.push(node);
    }
    if (node === endNode) {
      break;
    }
  }

  return nodes;
};

const HIGHLIGHT_WRAPPER_FLAG = "vtHighlightWrapper";

const wrap = (textNode, className) => {
  const span = document.createElement("span");
  span.className = className;
  span.dataset[HIGHLIGHT_WRAPPER_FLAG] = "true";
  textNode.parentNode.insertBefore(span, textNode);
  span.appendChild(textNode);

  return span;
};

/**
 * Highlights `searchText` inside `rootNode`, splitting text nodes where the match
 * starts and ends so a mid-node match can still be wrapped. Returns the wrapper
 * spans (empty when the text is not present), each flagged so unwrapHighlight can
 * undo it.
 */
export const highlightTextRange = (searchText, rootNode, className) => {
  const range = locateTextRange(searchText, rootNode);

  if (!range) {
    return [];
  }

  const { startNode, startOffset, endNode, endOffset } = range;
  const isSingleNode = startNode === endNode;

  // Split the tail off the end node first: splitting the start node would move
  // the end offset when both are the same node.
  if (endOffset < (endNode.textContent || "").length) {
    endNode.splitText(endOffset);
  }

  let firstNode = startNode;
  if (startOffset > 0) {
    firstNode = startNode.splitText(startOffset);
  }

  const lastNode = isSingleNode ? firstNode : endNode;

  return textNodesBetween(rootNode, firstNode, lastNode)
    .filter((node) => (node.textContent || "").length > 0)
    .map((node) => wrap(node, className));
};

/**
 * Removes a wrapper created by highlightTextRange, putting its text back where it
 * was. Normalising the parent re-merges the split text nodes, so repeated jumps
 * do not leave the text layer progressively more fragmented.
 */
export const unwrapHighlight = (span) => {
  const parent = span?.parentNode;

  if (!parent) {
    return;
  }

  while (span.firstChild) {
    parent.insertBefore(span.firstChild, span);
  }
  parent.removeChild(span);
  parent.normalize();
};

export const isHighlightWrapper = (element) =>
  Boolean(element?.dataset?.[HIGHLIGHT_WRAPPER_FLAG]);
